import { ApiError } from '../api/client';
import type { IconName } from '../components/ui/Icon';
import type { Tone } from '../components/ui/primitives/cx';
import type { OverviewExpenseCategory, OverviewVariation, OwnerOverview } from '../types/dashboard';
import { resultView, type ResultView } from './inicioView';
import {
  expenseChangeLabel,
  expenseChangeTone,
  formatMoney,
  MOVEMENT_DISCLAIMER,
  MOVEMENT_NOT_PROFIT,
  movementView,
  scopeNotes,
} from './ownerOverview';
import type { AuthStore, UserRole } from '../types/auth';
import type { AnyReport, BlockedReport, ReportKind } from '../types/reports';
import { dateToStoreISO, DEFAULT_STORE_TIMEZONE } from './timezones';

/**
 * Rediseño · Bloque F — lógica de presentación PURA de Reportes (sin React, testeable en node), como
 * `inicioView.ts` en el Bloque C y `newProduct.ts` en el E. Los componentes de `components/reports/` solo
 * dibujan lo que sale de aquí.
 *
 * Reportes reorganiza lo que el servidor YA entrega en `GET /api/dashboard/owner-overview`. Tres reglas
 * que este archivo no puede romper:
 *
 *   1. Ninguna cifra de dinero se calcula aquí. Todo importe llega del servidor y solo se le da formato.
 *      «Salió» es la suma de compras y gastos confirmados que ya hacía `movementView` antes de este
 *      bloque (el servidor no la entrega como una cifra); no se añade ninguna otra. Lo único que se
 *      compone es el ANCHO de las barras, que no es una cifra: es dibujo.
 *   2. Un dato que no existe no se inventa: ni «Entró» por método de pago, ni compras por categoría, ni
 *      la variación de «Salió», ni «¢ de cada peso», ni ticket promedio, ni filtro de tienda. Lo que
 *      falta está listado en `docs/UI_REDESIGN_BLOCK_F.md`.
 *   3. El resultado estimado pasa SIEMPRE por su sobre de confianza (`resultView` → `confidenceView`):
 *      sin sobre no hay número, y sus cuatro estados no se presentan con la misma certeza (B0-01).
 */

const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

// ─────────────────────────────────────────────────────────────────────────────
// Las siete preguntas (P-030)
// ─────────────────────────────────────────────────────────────────────────────

/** Las siete preguntas, todas reportes de verdad desde P-084. El id es el segundo segmento de su ruta. */
export type ReportId = 'negocio' | 'dinero' | ReportKind;

export interface ReportQuestion {
  key: string;
  title: string;
  description: string;
  icon: IconName;
  tone: Tone;
  /** El reporte que abre. */
  report: ReportId;
}

/**
 * Las siete preguntas de P-030, en su orden, con su icono y su color. R1 y R2 son los de P-069 (P-032, I2 e I3);
 * R3–R7 se activan en P-084 con datos reales (FUTURE_UPGRADES #38 cerrada): cada tarjeta abre su reporte.
 *
 * Las líneas de apoyo dicen lo que cada reporte SÍ responde: la de R2 no es la de P-030 («En qué gastas y
 * compras más») porque las compras no tienen categorías; la de R6 ya no promete «de qué tienes de más» (sin
 * máximos configurados no se puede afirmar); la de R5 responde con precios de compra observados.
 */
export const REPORT_QUESTIONS: readonly ReportQuestion[] = [
  { key: 'r1', title: '¿Cómo va mi negocio?', description: 'Lo que entró, lo que salió y lo que te quedó en el periodo.', icon: 'trend-up', tone: 'info', report: 'negocio' },
  { key: 'r2', title: '¿Dónde se va mi dinero?', description: 'En qué gastas más y qué cambió, con tus compras de insumos aparte.', icon: 'wallet', tone: 'danger', report: 'dinero' },
  { key: 'r3', title: '¿Qué productos me convienen?', description: 'Qué se vende más, qué genera más ingreso y qué deja más donde conoces el costo.', icon: 'heart', tone: 'warning', report: 'productos' },
  { key: 'r4', title: '¿Cuándo vendo más?', description: 'Tus días y horas fuertes, para planear turnos y producción.', icon: 'shift', tone: 'accent', report: 'horarios' },
  { key: 'r5', title: '¿Cómo van mis costos?', description: 'Cuánto te costó lo que vendiste, de cuánto sabes el costo y qué insumos cambiaron de precio.', icon: 'coins', tone: 'highlight', report: 'costos' },
  { key: 'r6', title: '¿Cómo está mi inventario?', description: 'Qué se está acabando, qué se mueve más y qué no se ha movido.', icon: 'inventory', tone: 'success', report: 'inventario' },
  { key: 'r7', title: '¿Cómo va la operación?', description: 'Cortes, diferencias de caja, anulaciones y pendientes del periodo.', icon: 'drawer', tone: 'info', report: 'operacion' },
];

/** El título de cada reporte real: su h1. */
export function reportTitle(id: ReportId): string {
  return REPORT_QUESTIONS.find((question) => question.report === id)!.title;
}

/** La línea de apoyo de cada reporte real, bajo su h1. */
export function reportLead(id: ReportId): string {
  return REPORT_QUESTIONS.find((question) => question.report === id)!.description;
}

/** Los reportes nuevos de P-084, que piden su propio endpoint; R1 y R2 siguen pidiendo `owner-overview`. */
export function isReportKind(id: ReportId): id is ReportKind {
  return id !== 'negocio' && id !== 'dinero';
}

// ─────────────────────────────────────────────────────────────────────────────
// El periodo
// ─────────────────────────────────────────────────────────────────────────────

export type PeriodPreset = 'hoy' | 'semana' | 'mes';
export type PeriodChoice = PeriodPreset | 'otro';

/** Lo que se le pide al servidor: siempre un rango explícito, como el Reportes de antes del bloque. */
export interface AppliedPeriod {
  choice: PeriodChoice;
  from: string;
  to: string;
}

/** Los periodos de P-030 (Hoy · Últimos 7 días · Este mes) y «Otro periodo», que el Reportes de antes ya permitía. */
export const PERIOD_CHOICES: ReadonlyArray<{ id: PeriodChoice; label: string }> = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'semana', label: 'Últimos 7 días' },
  { id: 'mes', label: 'Este mes' },
  { id: 'otro', label: 'Otro periodo' },
];

/**
 * El rango más largo que acepta el servidor, contado en días de calendario con los dos extremos
 * (`MAX_OVERVIEW_RANGE_DAYS` en `ownerOverviewService.ts`). Es un espejo para avisar ANTES de pedir: la
 * regla la sigue aplicando el servidor, y si aun así responde que el rango no cabe, se dice (§ error).
 */
export const MAX_REPORT_DAYS = 93;

const DAY_MS = 24 * 60 * 60 * 1_000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Medianoche UTC de una fecha 'YYYY-MM-DD' que existe de verdad (no un 31 de febrero), o `null`. */
function utcOf(iso: string): number | null {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const time = Date.UTC(year, month - 1, day);
  const back = new Date(time);
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null;
  return time;
}

/** 'YYYY-MM-DD' más (o menos) N días de calendario. Aritmética de fechas, no de horas: sin horario de verano. */
function addDays(iso: string, days: number): string {
  return new Date(utcOf(iso)! + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * HOY en la zona de la tienda, no en la del equipo: el mismo criterio que usaba el Reportes anterior
 * (`dateToStoreISO`). P-084 (Codex R1 · K8): la zona es la de la PRIMERA tienda financiera del perfil
 * (`referenceTimeZone`), la misma referencia con la que el servidor etiqueta el periodo (`scope.stores[0]`, en
 * el mismo orden por nombre); antes era siempre Ciudad de México.
 */
export function storeToday(now: Date = new Date(), timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  return dateToStoreISO(now, timeZone);
}

/**
 * La zona de referencia de Reportes: la primera tienda (en el orden del perfil, por nombre) donde la persona tiene
 * acceso financiero —todas para el dueño—, igual que `financialScopeService` → `scope.stores[0]` en el servidor.
 * Sin tiendas, la de siempre.
 */
export function referenceTimeZone(role: UserRole | undefined, stores: readonly AuthStore[]): string {
  const financial = role === 'OWNER' ? stores : role === 'MANAGER' ? stores.filter((store) => store.canViewFinancials) : [];
  return financial[0]?.timezone || DEFAULT_STORE_TIMEZONE;
}

/**
 * El rango de cada periodo de P-030. «Este mes» va del día 1 del mes de HOY —el de la tienda— hasta hoy:
 * el Reportes anterior armaba el día 1 con el reloj del equipo y luego lo pasaba a la zona de la tienda,
 * así que en un equipo adelantado a la tienda caía en el último día del mes anterior. Ahora sale de la
 * misma fecha que «hoy».
 */
export function presetPeriod(preset: PeriodPreset, today: string): AppliedPeriod {
  if (preset === 'hoy') return { choice: 'hoy', from: today, to: today };
  if (preset === 'semana') return { choice: 'semana', from: addDays(today, -6), to: today };
  return { choice: 'mes', from: `${today.slice(0, 8)}01`, to: today };
}

/** Por defecto, el mes en curso hasta hoy: el periodo con el que abría el Reportes anterior. */
export function defaultPeriod(now: Date = new Date(), timeZone: string = DEFAULT_STORE_TIMEZONE): AppliedPeriod {
  return presetPeriod('mes', storeToday(now, timeZone));
}

/** Días de calendario entre dos fechas, contando las dos. */
export function inclusiveDays(from: string, to: string): number {
  return Math.round((utcOf(to)! - utcOf(from)!) / DAY_MS) + 1;
}

export interface RangeProblem {
  /** El campo al que se le dice: es el que se marca como inválido y el que recibe el foco. */
  field: 'from' | 'to';
  message: string;
}

/** Lo que impide pedir un periodo elegido a mano, o `null` si se puede pedir. */
export function rangeProblem(from: string, to: string): RangeProblem | null {
  if (!from) return { field: 'from', message: 'Elige el día en que empieza.' };
  if (utcOf(from) === null) return { field: 'from', message: 'Escribe una fecha válida.' };
  if (!to) return { field: 'to', message: 'Elige el día en que termina.' };
  if (utcOf(to) === null) return { field: 'to', message: 'Escribe una fecha válida.' };
  if (from > to) return { field: 'to', message: '«Hasta» tiene que ser el mismo día que «Desde» o uno posterior.' };
  if (inclusiveDays(from, to) > MAX_REPORT_DAYS) return { field: 'to', message: `Elige un periodo de ${MAX_REPORT_DAYS} días o menos.` };
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fechas en palabras
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function partsOf(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split('-').map(Number);
  return { year: year ?? 0, month: month ?? 1, day: day ?? 1 };
}

/**
 * «5 de octubre», «1 al 5 de octubre», «28 de septiembre al 4 de octubre». El año se escribe cuando el
 * rango cruza de un año a otro o no es el de hoy. Las fechas son las que DEVUELVE el servidor: dicen qué
 * periodo describen las cifras, no qué se pidió.
 */
export function rangeText(from: string, to: string, today: string): string {
  const start = partsOf(from);
  const end = partsOf(to);
  const withYear = start.year !== end.year || end.year !== partsOf(today).year;
  const day = (part: typeof start) => `${part.day} de ${MONTHS[part.month - 1]}${withYear ? ` de ${part.year}` : ''}`;
  if (from === to) return day(start);
  if (start.year === end.year && start.month === end.month) return `${start.day} al ${day(end)}`;
  return `${day(start)} al ${day(end)}`;
}

/** «el 5 de octubre» o «del 1 al 5 de octubre»: el periodo como complemento de una frase. */
export function rangePhrase(from: string, to: string, today: string): string {
  return `${from === to ? 'el' : 'del'} ${rangeText(from, to, today)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lo que llegó del servidor
// ─────────────────────────────────────────────────────────────────────────────

export type ReportLoad =
  | { status: 'loading' }
  | { status: 'error'; title: string; description: string; canRetry: boolean }
  | { status: 'no-access'; message: string }
  | { status: 'ready'; overview: OwnerOverview }
  /** P-084: la respuesta de uno de los cinco reportes nuevos (`GET /api/dashboard/reports/:report`). */
  | { status: 'ready-report'; kind: ReportKind; report: AnyReport };

/** Las respuestas que todavía no tienen cifras: lo que dibuja `ReportsPending`. */
export type PendingLoad = Exclude<ReportLoad, { status: 'ready' } | { status: 'ready-report' }>;

/** La explicación cuando el servidor no manda bloque (204): la de una cuenta sin acceso financiero. */
export const NO_FINANCIAL_ACCESS = 'Tu cuenta no tiene acceso a la información financiera del negocio.';

/**
 * La respuesta del servidor, dicha como estado. Un 204 (sin bloque) o un alcance `NONE` son «sin
 * acceso», con la razón que da el servidor: nunca una pantalla en blanco ni unas cifras en cero.
 */
export function loadFromResponse(overview: OwnerOverview | null | undefined): ReportLoad {
  if (!overview) return { status: 'no-access', message: NO_FINANCIAL_ACCESS };
  if (overview.scope.mode === 'NONE') return { status: 'no-access', message: overview.scope.reason ?? NO_FINANCIAL_ACCESS };
  return { status: 'ready', overview };
}

/**
 * P-084 · La respuesta de un reporte nuevo, dicha como estado: un 204 (sin bloque) o un alcance `NONE` son «sin
 * acceso», con la razón del servidor, exactamente como `loadFromResponse`.
 */
export function loadFromReport(kind: ReportKind, response: AnyReport | BlockedReport | null | undefined): ReportLoad {
  if (!response) return { status: 'no-access', message: NO_FINANCIAL_ACCESS };
  if (response.meta.scope.mode === 'NONE' || 'blocked' in response) return { status: 'no-access', message: response.meta.scope.reason ?? NO_FINANCIAL_ACCESS };
  return { status: 'ready-report', kind, report: response };
}

/**
 * Un fallo, dicho como estado. Si el servidor rechazó el periodo (más de 93 días, o al revés), se dice eso
 * y no se ofrece «Reintentar»: pedir lo mismo daría lo mismo. Todo lo demás es «no pude traerlos»: no
 * se sabe nada de las cifras, así que no se muestra ninguna.
 */
export function loadFromError(error: unknown): ReportLoad {
  if (error instanceof ApiError && error.status === 400) {
    const code = error.payload?.error;
    if (code === 'OVERVIEW_RANGE_TOO_LARGE') {
      return { status: 'error', title: 'Revisa el periodo.', description: `Puedo mostrarte hasta ${MAX_REPORT_DAYS} días a la vez. Elige un periodo más corto.`, canRetry: false };
    }
    if (code === 'OVERVIEW_RANGE_INVALID') {
      return { status: 'error', title: 'Revisa el periodo.', description: 'Las fechas del periodo no son válidas.', canRetry: false };
    }
  }
  return { status: 'error', title: 'No pude traer tus números ahora.', description: 'Puede ser la conexión. Vuelve a intentarlo.', canRetry: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lo que comparten R1 y R2: el periodo, el alcance y la comparación
// ─────────────────────────────────────────────────────────────────────────────

export interface ReportContext {
  /** «Del 1 al 5 de octubre»: el periodo que el servidor dice que describen las cifras. */
  periodLabel: string;
  /** «Todas las tiendas», o las tiendas del gerente (lo decide el servidor). */
  scopeLabel: string;
  /** Lo que estas cifras NO incluyen (M-3) y las zonas horarias mixtas (H-2). */
  scopeNotes: string[];
  /**
   * Contra qué se compara: «del 26 al 30 de septiembre», el periodo anterior de la misma duración que
   * calcula el servidor. `null` cuando no hay con qué comparar.
   */
  comparedWith: string | null;
}

export const NO_COMPARISON = 'Todavía no tengo con qué comparar. En unas semanas te podré decir si vas mejor o peor.';
const NO_COMPARISON_SHORT = 'Todavía no tengo con qué comparar.';

export function reportContext(overview: OwnerOverview, today: string): ReportContext {
  const { period } = overview;
  const canCompare = Boolean(period.variation && period.previous && period.previousFromIso && period.previousToIso);
  return {
    periodLabel: upperFirst(rangePhrase(period.fromIso, period.toIso, today)),
    scopeLabel: overview.scope.label,
    scopeNotes: scopeNotes(overview),
    comparedWith: canCompare ? rangePhrase(period.previousFromIso!, period.previousToIso!, today) : null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Variaciones
// ─────────────────────────────────────────────────────────────────────────────

export interface Delta {
  direction: 'up' | 'down' | 'flat';
  /** «12 % más», «$500.00 menos», «Igual que antes». La flecha va aparte y es adorno. */
  text: string;
}

/**
 * Una variación del servidor, dicha en palabras. `percent`: el porcentaje que manda el servidor cuando
 * existe y redondeado no es 0 (con base cero manda `null`); si no, la diferencia en pesos. `money`:
 * siempre la diferencia en pesos. Aquí no se calcula ninguna: las dos llegan hechas.
 */
export function deltaOf(variation: OverviewVariation, mode: 'percent' | 'money'): Delta {
  const amount = Number(variation.amount);
  if (amount === 0) return { direction: 'flat', text: 'Igual que antes' };
  const word = amount > 0 ? 'más' : 'menos';
  const rounded = variation.percentage === null ? 0 : Math.round(Math.abs(variation.percentage));
  const magnitude = mode === 'percent' && rounded >= 1 ? `${rounded} %` : formatMoney(Math.abs(amount));
  return { direction: amount > 0 ? 'up' : 'down', text: `${magnitude} ${word}` };
}

// ─────────────────────────────────────────────────────────────────────────────
// R1 · ¿Cómo va mi negocio?
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Un trozo de frase; `strong` va en negritas. `amount`: es una cifra, y no se parte al final de un renglón
 * (el «−» de un negativo no se queda solo). Un nombre en negritas —una categoría— sí puede partirse.
 */
export interface Segment {
  text: string;
  strong?: boolean;
  amount?: boolean;
}

export interface FlowView {
  key: 'in' | 'out' | 'kept';
  label: 'Entró' | 'Salió' | 'Te quedó';
  icon: IconName;
  amountLabel: string;
  /** Lo que la cifra no dice con el color: un negativo, en palabras. */
  note: string | null;
  /** La variación contra el periodo anterior, o `null` cuando no la hay (o no existe como cifra). */
  delta: Delta | null;
}

/**
 * La barra de P-030: de cada peso que entró, cuánto salió y cuánto quedó. Es DIBUJO —el ancho de dos
 * cifras del servidor—, oculto al lector de pantalla; ninguna proporción se escribe («¢ de cada peso»
 * queda fuera por B0-01). `null` cuando no entró nada que repartir.
 */
export interface ProportionView {
  outPercent: number;
  keptPercent: number;
  /** Salió tanto o más de lo que entró: la barra es toda «Salió». */
  overflow: boolean;
}

export interface BusinessReportView {
  say: Segment[];
  flows: [FlowView, FlowView, FlowView];
  /** Contra qué comparan las flechas, o por qué no hay flechas; `null` con «Comparar» apagado. */
  comparison: string | null;
  proportion: ProportionView | null;
  /** «Ver de dónde salió»: lo que forma «Entró». Por método de pago no existe en este contrato. */
  inflow: { totalLabel: string; note: string };
  /** Lo que forma «Salió»: las compras (sin categorías: no las hay) y los gastos por categoría. */
  outflow: {
    purchasesLabel: string;
    expensesLabel: string;
    expenses: Array<{ name: string; amountLabel: string }>;
    totalLabel: string;
    noExpensesNote: string | null;
  };
  disclaimer: string;
  notProfit: string;
  /** El resultado estimado, con la presentación del Inicio (B0-01) y el periodo del reporte. */
  result: ResultView;
}

export const INFLOW_NOTE = 'Incluye todas tus ventas, sin importar desde dónde se cobraron. Las anuladas no cuentan y las devoluciones restan.';
export const RESULT_INDEPENDENT = 'Entró, Salió y Te quedó no dependen de esto: vienen de lo que registraste.';
export const WHY_DIFFERENT_TITLE = '¿Por qué son números distintos?';
export const WHY_DIFFERENT: ReadonlyArray<Segment[]> = [
  [{ text: 'Te quedó', strong: true }, { text: ' es el dinero que entró menos el que salió en el periodo: tus ventas, tus compras de insumos y tus gastos confirmados.' }],
  [{ text: 'Después de costos y gastos', strong: true }, { text: ' es tu ' }, { text: 'resultado estimado', strong: true }, { text: ': compara lo que vendiste con lo que te costó hacerlo —aunque los insumos los hayas comprado otro día— y le resta los gastos confirmados.' }],
  [{ text: 'Ninguno de los dos es un corte contable.' }],
];

/**
 * `compare`: «Comparar con el periodo anterior» de P-030 (P-032, I9: solo de pantalla). Apagado, no se
 * dibuja ninguna flecha ni se dice ninguna comparación; las cifras y lo que se pide al servidor no cambian.
 */
export function businessReportView(overview: OwnerOverview, today: string, { compare = true }: { compare?: boolean } = {}): BusinessReportView {
  const { period } = overview;
  const movement = movementView(overview);
  const context = reportContext(overview, today);
  const variation = compare && period.variation && period.previous ? period.variation : null;

  const inflow = Number(period.current.netSalesAmount);
  const outflow = Number(period.current.confirmedPurchasesAmount) + Number(period.current.confirmedExpensesAmount);
  const kept = Number(period.current.moneyMovementResult);
  const keptLabel = formatMoney(period.current.moneyMovementResult);

  const flows: [FlowView, FlowView, FlowView] = [
    {
      key: 'in', label: 'Entró', icon: 'arrow-in', amountLabel: movement.inflow.amountLabel,
      note: inflow < 0 ? 'Hubo más devoluciones que ventas.' : null,
      delta: variation ? deltaOf(variation.netSales, 'percent') : null,
    },
    // El servidor no entrega la variación de «Salió» como una cifra; sumar las de compras y gastos sería
    // una cifra nueva. Va sin flecha.
    { key: 'out', label: 'Salió', icon: 'arrow-out', amountLabel: movement.outflow.amountLabel, note: null, delta: null },
    {
      key: 'kept', label: 'Te quedó', icon: 'money', amountLabel: keptLabel,
      note: kept < 0 ? 'Salió más de lo que entró.' : null,
      delta: variation ? deltaOf(variation.result, 'money') : null,
    },
  ];

  const lead = upperFirst(rangePhrase(period.fromIso, period.toIso, today));
  let say: Segment[];
  // Sin ninguna venta registrada se dice; de compras y gastos solo consta que suman $0.00. Un neto en $0
  // con ventas y devoluciones que se compensan NO es «sin movimientos»: va la frase de siempre.
  if (inflow === 0 && outflow === 0 && period.current.saleCount === 0) {
    say = [{ text: `${lead} no se registraron ventas, y las compras y los gastos confirmados suman ${formatMoney(0)}.` }];
  } else {
    say = [
      { text: `${lead} entraron ` }, { text: movement.inflow.amountLabel, strong: true, amount: true },
      { text: ' y salieron ' }, { text: movement.outflow.amountLabel, strong: true, amount: true },
      { text: kept < 0 ? '. Salió más de lo que entró: te quedaron ' : '. Te quedaron ' }, { text: keptLabel, strong: true, amount: true },
    ];
    if (variation && context.comparedWith) {
      const difference = Number(variation.result.amount);
      const against = `que en el periodo anterior (${context.comparedWith})`;
      say.push({ text: difference === 0 ? `, igual ${against}.` : `, ${formatMoney(Math.abs(difference))} ${difference > 0 ? 'más' : 'menos'} ${against}.` });
    } else {
      say.push({ text: compare ? `. ${NO_COMPARISON_SHORT}` : '.' });
    }
  }

  const proportion: ProportionView | null = inflow > 0
    ? outflow >= inflow
      ? { outPercent: 100, keptPercent: 0, overflow: true }
      : { outPercent: Math.round((outflow / inflow) * 100), keptPercent: 100 - Math.round((outflow / inflow) * 100), overflow: false }
    : null;

  const expenses = overview.expenses.categories
    .filter((row) => Number(row.currentAmount) !== 0)
    .slice()
    .sort((left, right) => Number(right.currentAmount) - Number(left.currentAmount))
    .map((row) => ({ name: row.category, amountLabel: formatMoney(row.currentAmount) }));

  const result = { ...resultView(overview), periodLabel: context.periodLabel };

  return {
    say,
    flows,
    comparison: !compare ? null : context.comparedWith ? `Las flechas comparan con el periodo anterior, ${context.comparedWith}.` : NO_COMPARISON,
    proportion,
    inflow: { totalLabel: movement.inflow.amountLabel, note: INFLOW_NOTE },
    outflow: {
      purchasesLabel: formatMoney(period.current.confirmedPurchasesAmount),
      expensesLabel: formatMoney(period.current.confirmedExpensesAmount),
      expenses,
      totalLabel: movement.outflow.amountLabel,
      noExpensesNote: movement.noExpensesNote,
    },
    disclaimer: MOVEMENT_DISCLAIMER,
    notProfit: MOVEMENT_NOT_PROFIT,
    result,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// R2 · ¿Dónde se va mi dinero?
// ─────────────────────────────────────────────────────────────────────────────

export interface ExpenseRowView {
  name: string;
  amountLabel: string;
  /** Ancho de la barra, de 0 a 100, relativo a la mayor de la lista. Una categoría en $0 no tiene barra. */
  barPercent: number;
  /**
   * «$1,200.00 más», «$300.00 menos», «apareció este periodo», «sin historia para comparar», «igual que
   * antes» (NUEVO-3). El sentido va en palabras; la flecha (`changeDirection`) es adorno.
   */
  changeLabel: string;
  changeDirection: 'up' | 'down' | 'flat';
}

export interface MoneyReportView {
  /** La frase de arriba, o `null` cuando no hay gastos. */
  say: Segment[] | null;
  totalLabel: string;
  /** La variación del total de gastos, o `null` sin periodo comparable. */
  totalDelta: Delta | null;
  /** Contra qué periodo comparan los cambios, o `null` sin comparación. */
  comparison: string | null;
  /** Hasta cinco categorías, en el orden del servidor: por impacto en pesos. */
  rows: ExpenseRowView[];
  purchasesLabel: string;
  disclaimer: string;
}

export const EXPENSES_EMPTY_TITLE = 'Todavía no hay gastos confirmados en este periodo.';
export const EXPENSES_EMPTY_TEXT = 'Cuando confirmes uno, aquí verás en qué se va tu dinero.';
export const PURCHASES_NOTE = 'Comprar insumos no es un gasto del periodo: es convertir dinero en inventario. Por eso va aparte.';
export const EXPENSES_NO_HISTORY = 'Todavía no hay suficiente historia de gastos para comparar con el periodo anterior.';

/**
 * La frase de R2: la de antes del bloque, con la categoría que encabeza la lista del servidor. Esa lista
 * va por impacto en pesos (el cambio) y, sin comparación, por importe —el servidor la manda así—, de modo
 * que «la más grande» solo se dice cuando de verdad lo es. Única frase nueva: una categoría que este
 * periodo quedó en $0 ya no dice «Gastaste $0.00 en…».
 */
function moneySay(top: OverviewExpenseCategory, comparable: boolean): Segment[] {
  const amount = formatMoney(top.currentAmount);
  const change = Number(top.changeAmount);
  if (!comparable) {
    return [{ text: 'Tu categoría más grande es ' }, { text: top.category, strong: true }, { text: `, con ${amount}. ${EXPENSES_NO_HISTORY}` }];
  }
  if (top.isNew) return [{ text: 'Este periodo apareció ' }, { text: top.category, strong: true }, { text: ` con ${amount}.` }];
  if (change === 0) {
    return [{ text: 'Tu categoría más grande es ' }, { text: top.category, strong: true }, { text: `, con ${amount}. No cambió contra el periodo anterior.` }];
  }
  if (Number(top.currentAmount) === 0) {
    return [{ text: 'Este periodo no hubo gastos de ' }, { text: top.category, strong: true }, { text: `: ${formatMoney(Math.abs(change))} menos que el periodo anterior. Es tu categoría que más bajó.` }];
  }
  return [
    { text: `Gastaste ${amount} en ` }, { text: top.category, strong: true },
    { text: `, ${formatMoney(Math.abs(change))} ${change > 0 ? 'más' : 'menos'} que el periodo anterior. Es tu categoría que más ${change > 0 ? 'creció' : 'bajó'}.` },
  ];
}

/**
 * El cambio de una categoría, con las reglas de NUEVO-3 (`expenseChangeLabel`): sin comparación, «sin
 * historia para comparar»; nueva, «apareció este periodo»; igual, «igual que antes». Un cambio en pesos se
 * dice con «más» o «menos»: la etiqueta de antes era solo «▲ $1,200.00», y el triángulo era lo único que
 * decía hacia dónde.
 */
function expenseChange(row: OverviewExpenseCategory, comparable: boolean): Pick<ExpenseRowView, 'changeLabel' | 'changeDirection'> {
  const direction = expenseChangeTone(row, comparable);
  if (!comparable || row.isNew || direction === 'flat') return { changeLabel: expenseChangeLabel(row, comparable), changeDirection: 'flat' };
  const change = Number(row.changeAmount);
  return { changeLabel: `${formatMoney(Math.abs(change))} ${change > 0 ? 'más' : 'menos'}`, changeDirection: direction };
}

export function moneyReportView(overview: OwnerOverview, today: string): MoneyReportView {
  const { expenses, period } = overview;
  const { comparedWith } = reportContext(overview, today);
  const comparable = expenses.comparable;
  const top = expenses.categories.slice(0, 5);
  const scale = Math.max(1, ...top.map((row) => Number(row.currentAmount)));
  const totalChange: OverviewVariation = { amount: expenses.totalChangeAmount, percentage: expenses.totalChangePercent };

  return {
    say: top[0] ? moneySay(top[0], comparable) : null,
    totalLabel: formatMoney(expenses.totalAmount),
    totalDelta: comparable && top.length > 0 ? deltaOf(totalChange, 'money') : null,
    // Los gastos se comparan contra el mismo periodo anterior que las ventas; si el servidor no da sus
    // fechas (sin historia de ventas suficiente), se dice cuál es sin inventarlas.
    comparison: comparable && top.length > 0
      ? comparedWith ? `Los cambios comparan con el periodo anterior, ${comparedWith}.` : 'Los cambios comparan con el periodo anterior de la misma duración.'
      : null,
    rows: top.map((row) => {
      const current = Number(row.currentAmount);
      return {
        name: row.category,
        amountLabel: formatMoney(row.currentAmount),
        barPercent: current > 0 ? Math.max(2, Math.round((current / scale) * 100)) : 0,
        ...expenseChange(row, comparable),
      };
    }),
    purchasesLabel: formatMoney(period.current.confirmedPurchasesAmount),
    disclaimer: MOVEMENT_DISCLAIMER,
  };
}
